/***
 *
 *   DASHBOARD
 *   Template dashboard example demonstrating various components inside a view.
 *
 **********/

import { useRef, useContext, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  ViewContext,
  Card,
  Stat,
  Table,
  Grid,
  Row,
  Animate,
  Feedback,
  useAPI,
} from "components/lib";
import SemiCircleProgressBar from "components/progress/progressbar";
import { Line } from "react-chartjs-2";
import { useState } from "react";
import { useCallback } from "react";
import { createPortal } from "react-dom";
import { Avatar } from "components/lib";

// API Color Scheme for all scopes
const API_COLOR_SCHEMES = {
  'maps': "rgb(91 147 255)",
  'autocomplete': "rgb(255 214 107)",
  'geocode': "rgb(53 150 14)",
  'directions': "rgb(255 99 132)",
  'map_matching': "rgb(54 162 235)",
  'optimize': "rgb(255 205 86)",
  'matrix': "rgb(75 192 192)",
  'isochrone': "rgb(153 102 255)",
  'elevation': "rgb(255 159 64)",
  'navigation': "rgb(201 203 207)",
  'tiles': "rgb(102 187 106)",
  'staticImages': "rgb(233 30 99)"
};

const data = {
  labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
  datasets: [
    {
      label: "Maps API",
      data: [33, 53, 85, 41, 44, 65],
      fill: false,
      tension: 0.4,
      borderColor: "rgba(91, 147, 255, 0.8)",
      backgroundColor: "rgba(91, 147, 255, 0.1)",
      pointBackgroundColor: "rgba(91, 147, 255, 0.9)",
      pointBorderColor: "rgba(255, 255, 255, 0.8)",
      pointBorderWidth: 2,
      pointRadius: 6,
      pointHoverRadius: 8,
      shadowOffsetX: 0,
      shadowOffsetY: 2,
      shadowBlur: 8,
      shadowColor: "rgba(91, 147, 255, 0.3)",
    },
    {
      label: "Mobile SDK",
      data: [13, 25, 65, 51, 44, 100],
      fill: false,
      tension: 0.4,
      borderColor: "rgba(255, 214, 107, 0.8)",
      backgroundColor: "rgba(255, 214, 107, 0.1)",
      pointBackgroundColor: "rgba(255, 214, 107, 0.9)",
      pointBorderColor: "rgba(255, 255, 255, 0.8)",
      pointBorderWidth: 2,
      pointRadius: 6,
      pointHoverRadius: 8,
      shadowOffsetX: 0,
      shadowOffsetY: 2,
      shadowBlur: 8,
      shadowColor: "rgba(255, 214, 107, 0.3)",
    },
    {
      label: "Geocoding",
      data: [3, 25, 45, 51, 64, 16],
      fill: false,
      tension: 0.4,
      borderColor: "rgba(53, 150, 14, 0.8)",
      backgroundColor: "rgba(53, 150, 14, 0.1)",
      pointBackgroundColor: "rgba(53, 150, 14, 0.9)",
      pointBorderColor: "rgba(255, 255, 255, 0.8)",
      pointBorderWidth: 2,
      pointRadius: 6,
      pointHoverRadius: 8,
      shadowOffsetX: 0,
      shadowOffsetY: 2,
      shadowBlur: 8,
      shadowColor: "rgba(53, 150, 14, 0.3)",
    },
    {
      label: "Search API",
      data: [43, 15, 0, 51, 54, 6],
      fill: false,
      tension: 0.4,
      borderColor: "rgba(255, 99, 132, 0.8)",
      backgroundColor: "rgba(255, 99, 132, 0.1)",
      pointBackgroundColor: "rgba(255, 99, 132, 0.9)",
      pointBorderColor: "rgba(255, 255, 255, 0.8)",
      pointBorderWidth: 2,
      pointRadius: 6,
      pointHoverRadius: 8,
      shadowOffsetX: 0,
      shadowOffsetY: 2,
      shadowBlur: 8,
      shadowColor: "rgba(255, 99, 132, 0.3)",
    },
  ],
};
export function Dashboard({ t }) {
  // context
  const viewContext = useContext(ViewContext);
  const [users, setUsers] = useState([]);
  const [areUsersFormatted, setAreUsersFormatted] = useState(false);
  const MONTH_IN_MILLISECONDS = 2.628e+9;
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [visibleDatasets, setVisibleDatasets] = useState({});

  const [isDarkMode, setIsDarkMode] = useState(
    JSON.parse(localStorage.getItem("dark_mode")) || false
  );

  // Update state whenever localStorage changes
  useEffect(() => {
    const handleStorageChange = () => {
      setIsDarkMode(JSON.parse(localStorage.getItem("dark_mode")) || false);
    };

    // Listen for changes in localStorage
    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  const getMonthYear = (label) => {
    if (!label) return '';
    try {
      if (typeof label === 'number') {
        const date = new Date(label);
        return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      }
      const match = label.match(/(\w+)\s+\d+\s+(\d{4})/);
      return match ? `${match[1]} ${match[2]}` : '';
    } catch (e) {
      return '';
    }
  };

  const chartOptions = useMemo(
    () => ({
      cubicInterpolationMode: true,
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false, // Custom legend will be implemented separately
        },
        tooltip: {
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(10px)',
          borderColor: 'rgba(255, 255, 255, 0.2)',
          borderWidth: 1,
          titleColor: 'rgba(255, 255, 255, 0.9)',
          bodyColor: 'rgba(255, 255, 255, 0.8)',
          cornerRadius: 12,
          padding: 16,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
        },
      },
      layout: {
        padding: {
          top: 20,
          bottom: 20,
          left: 20,
          right: 20,
        },
      },
      scales: {
        x: {
          border: {
            display: false,
          },
          ticks: {
            color: "rgba(255, 255, 255, 0.7)",
            font: {
              size: window.innerWidth < 768 ? 11 : 13,
              family: 'Poppins, sans-serif',
              weight: '300',
            },
            maxRotation: 0,
            minRotation: 0,
            padding: 15,
            callback: function(value, index, ticks) {
              const label = this.getLabelForValue(value);
              if (!label) return label;
              const match = label.match(/\w+\s+(\d+)\s+\d{4}/);
              return match ? match[1] : label;
            }
          },
          grid: {
            color: "rgba(255, 255, 255, 0.1)",
            lineWidth: 1,
            drawBorder: false,
            drawTicks: false,
          },
        },
        y: {
          border: {
            display: false,
          },
          ticks: {
            color: "rgba(255, 255, 255, 0.7)",
            font: {
              size: window.innerWidth < 768 ? 11 : 13,
              family: 'Poppins, sans-serif',
              weight: '300',
            },
            padding: 15,
          },
          grid: {
            color: "rgba(255, 255, 255, 0.08)",
            lineWidth: 1,
            drawBorder: false,
            drawTicks: false,
          },
        },
      },
      elements: {
        point: {
          radius: 6,
          hoverRadius: 8,
          borderWidth: 3,
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          borderColor: 'rgba(255, 255, 255, 0.3)',
          shadowOffsetX: 0,
          shadowOffsetY: 4,
          shadowBlur: 8,
          shadowColor: 'rgba(0, 0, 0, 0.3)',
        },
        line: {
          tension: 0.4,
          borderWidth: 3,
          fill: false,
        },
      },
      interaction: {
        intersect: false,
        mode: 'index',
      },
    }),
    [isDarkMode]
  );

  // show welcome message
  useEffect(() => {
    const hasShownWelcomeMessage = localStorage.getItem(
      "hasShownWelcomeMessage"
    );

    if (!hasShownWelcomeMessage) {
      viewContext.notification({
        title: t("dashboard.message.title"),
        description: t("dashboard.message.text"),
      });

      // Set the flag in local storage
      localStorage.setItem("hasShownWelcomeMessage", "true");
    }
  }, [viewContext]);

  const res = useAPI("/api/demo");
  const { stats: unfilteredStats, revenue } = res.data || {};
  
  const dontShowCredits = true; // Hide credits section since upcoming invoice is removed

  const TOTAL_DISCOUNT_AMOUNT = 0;
  const AMOUNT_UTILIZED = 0;

  let stats = null;
  
  if (dontShowCredits) {
    stats = unfilteredStats?.filter(item => item.label != 'this month');
  } else {
    stats = unfilteredStats
  }

  // Add icons to stats based on their labels
  if (stats) {
    stats = stats.map(stat => {
      let icon = null;
      
      // Determine icon based on label content
      if (stat.label?.toLowerCase().includes('map') || stat.label?.toLowerCase().includes('Maps Used')) {
        icon = 'map';
      } else if (stat.label?.toLowerCase().includes('api') || stat.label?.toLowerCase().includes('call') || stat.label?.toLowerCase().includes('request')) {
        icon = 'activity';
      } else if (stat.label?.toLowerCase().includes('project') || stat.label?.toLowerCase().includes('Active Projects')) {
        icon = 'folder';
      } else if (stat.label?.toLowerCase().includes('user') || stat.label?.toLowerCase().includes('member')) {
        icon = 'users';
      } else if (stat.label?.toLowerCase().includes('key')) {
        icon = 'key';
      }
      
      return {
        ...stat,
        icon: icon
      };
    });
  }


  // console.log(users, "users");
  const fetchUsers = useAPI("/api/account/users");

  // format users and invites on initial load
  useEffect(() => {
    if (fetchUsers.data?.users) {
      const formatted = formatUsers({
        users: fetchUsers.data.users,
        invites: fetchUsers.data.invites,
      });

      setUsers([
        ...(formatted.users?.length ? formatted.users : []),
        ...(formatted.invites?.length ? formatted.invites : []),
      ]);
    }
  }, [fetchUsers]);

  useEffect(() => {
    console.log('users', users);
    setAreUsersFormatted(true);
  }, [users]);

  const formatUsers = useCallback(
    ({ users, invites }) => {
      let formattedUsers, formattedInvites;

      if (users?.length) {
        formattedUsers = users.map((user) => {
          const { avatar, verified, ...rest } = user;

          return {
            avatar: user.avatar ? <Avatar src={user.avatar} /> : null,
            ...rest,
            status: verified
              ? t("account.users.status.verified")
              : t("account.users.status.registered"),
          };
        });
      }

      if (invites?.length) {
        formattedInvites = invites.map((invite) => {
          return {
            avatar: <Avatar fallback={invite.email.charAt(0)} />,
            email: invite.email,
            permission: invite.permission || "user",
          };
        });
      }

      return { users: formattedUsers, invites: formattedInvites };
    },
    [t]
  );

  useEffect(() => {
    if (viewContext.dateRange) {
      // Trigger a refresh
      console.log({
        "viewContext.dateRange": viewContext.dateRange,
        "dateRange.start": viewContext.dateRange.start,
        "formatted": viewContext.dateRange.start ? new Date(viewContext.dateRange.start).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : 'No start date'
      })
      setRefreshTrigger((prev) => prev + 1);
    }
  }, [viewContext.dateRange]);

  const stripeDetails = useAPI(`/api/stripe/usage?startDate=${viewContext.dateRange.start}&endDate=${viewContext.dateRange.end}&_t=${refreshTrigger}`);
  const subscriptionDetails = useAPI('/api/stripe/subscription-details');
  const mapsData = useAPI('/api/studio/styles/get-all');
  // Process maps data to get first 2
  const mapList = useMemo(() => {
    if (!mapsData?.data?.files) return [];
    return mapsData.data.files.slice(0, 2).map(fileName => ({
      id: fileName.split("/")[1],
      image: "/assets/map.png",
      title: fileName.split("/")[1],
      fullName: fileName
    }));
  }, [mapsData]);

  // Access token for map styles
  const accessToken = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VySWQiOiI5YmQzZjJkZi0yOWUwLTQxNDgtOThhMC0wOTQzNDE2ZTkxNmIiLCJzY29wZSI6WyJtYXBzIiwic2VhcmNoIl0sImlhdCI6MTczODU2NzM2OH0.WMfRgaCGg_NSNWY709gTU4mcQ_lFyHylJdR3bmONabQ";

  // Load MapMetrics GL library and initialize maps
  useEffect(() => {
    if (!mapList.length) return;

    const initializeMaps = () => {
      if (!window.mapmetricsgl) {
        console.error("MapMetrics library not available");
        return;
      }

      mapList.forEach((map) => {
        const mapContainer = document.getElementById(`dashboard-map-${map.id}`);
        if (mapContainer) {
          try {
            const userMap = new window.mapmetricsgl.Map({
              container: `dashboard-map-${map.id}`,
              style: `https://blue-band-4da3.jim9710.workers.dev/styles/?fileName=${map.fullName}&token=${accessToken}`,
              zoom: 11,
              center: [2.349902, 48.852966],
              attributionControl: false,
              logo: false,
              interactive: false
            });

            userMap.scrollZoom.disable();
            userMap.doubleClickZoom.disable();
            userMap.touchZoomRotate.disable();
            userMap.dragPan.disable();
            userMap.dragRotate.disable();
            userMap.keyboard.disable();
            userMap.boxZoom.disable();
            userMap.getCanvas().style.cursor = "pointer";
          } catch (error) {
            console.error(`Error initializing map ${map.id}:`, error);
          }
        }
      });
    };

    window.onMapMetricsLoaded = function () {
      initializeMaps();
    };

    const cssLink = document.createElement("link");
    cssLink.rel = "stylesheet";
    cssLink.href = "https://cdn.mapmetrics-atlas.net/versions/latest/mapmetrics-gl.css";
    document.head.appendChild(cssLink);

    const customStyle = document.createElement("style");
    customStyle.textContent = `
      .mapmetrics-logo,
      .mapmetricsgl-logo,
      .mapmetrics-gl-logo,
      [class*="logo"] {
        display: none !important;
      }
    `;
    document.head.appendChild(customStyle);

    const script = document.createElement("script");
    script.src = "https://cdn.mapmetrics-atlas.net/versions/latest/mapmetrics-gl.js";
    script.async = false;
    script.defer = false;
    script.onload = () => {
      if (window.mapmetricsgl) {
        initializeMaps();
      }
    };
    script.onerror = (error) => {
      console.error("Failed to load MapMetrics library:", error);
    };

    document.body.appendChild(script);

    return () => {
      if (cssLink.parentNode) document.head.removeChild(cssLink);
      if (customStyle.parentNode) customStyle.parentNode.removeChild(customStyle);
      if (script.parentNode) script.parentNode.removeChild(script);
      delete window.onMapMetricsLoaded;
    };
  }, [mapList, accessToken]);

  // Prevent horizontal scroll on mount
  useEffect(() => {
    document.body.style.overflowX = 'hidden';
    document.documentElement.style.overflowX = 'hidden';
    return () => {
      document.body.style.overflowX = 'auto';
      document.documentElement.style.overflowX = 'auto';
    };
  }, []);

  return (
    <div className="min-h-screen w-full overflow-x-hidden">
      <div className="py-3 w-full box-border px-6 overflow-x-hidden">
        <Animate type="pop" className="space-y-4 w-full">
          {/* Stats Grid - Full Width */}
          <Grid max={3} className="gap-3">
            {stats?.length &&
              stats.map((stat) => {
                return (
                  <Card
                    key={stat.label}
                    loading={stat.loading}
                    className="p-3 h-full flex flex-col"
                  >
                    <Stat {...stat} stripeDetails={stripeDetails} t={t} />
                  </Card>
                );
              })}
          </Grid>

          {/* Main Dashboard Layout - Flexbox */}
          <div className="flex flex-col xl:flex-row gap-3 min-h-[650px] w-full overflow-x-hidden">
            {/* Left Column - Chart Area */}
            <div className="flex-1 min-w-0 overflow-x-hidden">
              <Card title="Requests by API" className="p-4 h-full flex flex-col w-full overflow-x-hidden">
                <div className="text-sm text-white/60 mb-2 font-light tracking-wide">
                  {viewContext.dateRange && viewContext.dateRange.start ? (
                    new Date(viewContext.dateRange.start).toLocaleDateString('en-US', { 
                      month: 'long', 
                      year: 'numeric' 
                    })
                  ) : (
                    new Date().toLocaleDateString('en-US', { 
                      month: 'long', 
                      year: 'numeric' 
                    })
                  )}
                </div>
                <div className="flex-1 min-h-[500px] w-full overflow-x-hidden">
                  <Line
                    type="line"
                    showlegend={data.toString()}
                    options={chartOptions}
                    loading=""
                    data={
                      stripeDetails &&
                      stripeDetails?.data &&
                      stripeDetails?.data?.usageHistory
                        ? {
                            ...stripeDetails?.data?.usageHistory,
                            labels: stripeDetails?.data?.usageHistory?.labels?.map(ts => {
                              const date = new Date(ts);
                              return (`${date?.toLocaleString('en-US', { month: 'short' })} ${date.getDate()} ${date.getFullYear()}`
                              + ', '
                              + new Date(ts).toLocaleTimeString('en-US', { hour: 'numeric', hour12: true }))
                            }),
                            datasets: stripeDetails?.data?.usageHistory?.datasets?.filter((dataset, index) =>
                              visibleDatasets[index] !== false
                            )
                          }
                        : {
                            ...data,
                            datasets: data.datasets.filter((dataset, index) =>
                              visibleDatasets[index] !== false
                            )
                          }
                    }
                  />
                </div>
                
                {/* Custom Glassmorphic Legend */}
                <div className="mt-4 flex flex-wrap gap-3 justify-center">
                  {(stripeDetails?.data?.usageHistory?.datasets || data.datasets).map((dataset, index) => {
                    const isVisible = visibleDatasets[index] !== false;
                    return (
                      <div
                        key={index}
                        onClick={() => setVisibleDatasets(prev => ({
                          ...prev,
                          [index]: !isVisible
                        }))}
                        className={`flex items-center gap-2 px-3 py-2 glass-card dark:glass-card-dark rounded-lg border cursor-pointer transition-all duration-300 hover:scale-105 ${
                          isVisible ? 'border-white/10 opacity-100' : 'border-white/5 opacity-40'
                        }`}
                      >
                        <div
                          className="w-3 h-3 rounded-full transition-all duration-300"
                          style={{
                            backgroundColor: isVisible ? dataset.borderColor : 'transparent',
                            border: `2px solid ${dataset.borderColor}`,
                            boxShadow: isVisible ? `0 0 8px ${dataset.shadowColor}` : 'none'
                          }}
                        />
                        <span className={`text-sm font-light poppins-light transition-colors duration-300 ${
                          isVisible ? 'text-white/80' : 'text-white/40'
                        }`}>
                          {dataset.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </Card>
            </div>
            
            {/* Right Column - Sidebar Cards */}
            <div className="xl:w-64 xl:flex-shrink-0 flex flex-col gap-3">
              {dontShowCredits ? null : (          
                <Card title={t("dashboard.credits.title")} className="p-3 h-fit">
                  <div className="text-center mb-4">
                    <SemiCircleProgressBar
                      containerId="container1"
                      value={
                        AMOUNT_UTILIZED && TOTAL_DISCOUNT_AMOUNT
                          ? 100 - (100 - ((Math.trunc(AMOUNT_UTILIZED) / Math.trunc(TOTAL_DISCOUNT_AMOUNT)) * 100))
                          : 0
                      }
                    />
                    <h2 className="mt-[-10px] text-primary text-[24px] font-bold leading-[1]">
                      <span className="block text-[12px] font-[400] dark:text-slate-400 text-opacity-20 text-opacity-70">
                        {t("dashboard.credits.credit_left")}
                      </span>
                    </h2>
                    <h6 className="text-primary text-opacity-70 text-[12px] font-bold mt-2 dark:text-white">
                      {stripeDetails.loading == true ? (
                        <>Loading..</>
                      ) : (
                        <>
                          ${AMOUNT_UTILIZED?.toLocaleString()}{" "}
                          {t("dashboard.credits.out")} ${TOTAL_DISCOUNT_AMOUNT?.toLocaleString()}{" "}
                          {t("dashboard.credits.used")}
                        </>
                      )}
                    </h6>
                  </div>
                  <div className="text-primary dark:text-white text-opacity-70 text-[12px] font-bold">
                    {t("dashboard.credits.total_cost")}: $0.00
                    <br />
                    Days remaining until credits expire: {
                    !subscriptionDetails?.loading
                      ? Math.ceil((new Date(new Date(subscriptionDetails?.data?.subscribed_at).getTime() + MONTH_IN_MILLISECONDS) - new Date()) / (1000 * 60 * 60 * 24))
                      : "Loading.."
                      }
                  </div>
                </Card>
              )}
              
              <Card title="Team Members" className="p-4 flex flex-col min-h-[200px]">
                <div className="flex-1">
                  {users?.length > 0 ? (
                    <div className="space-y-3">
                      {users.slice(0, 3).map((user, index) => (
                        <div key={index} className="flex items-center gap-3 p-2 glass-card dark:glass-card-dark rounded-lg border border-white/5">
                          <div className="w-8 h-8 rounded-full glass-card dark:glass-card-dark border border-white/10 flex items-center justify-center">
                            {user.avatar || (
                              <span className="text-white/70 text-xs font-medium">
                                {user.email?.charAt(0)?.toUpperCase()}
                              </span>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-white/90 text-sm font-medium poppins-medium truncate">
                              {user.email || user.name}
                            </p>
                            <p className="text-white/60 text-xs poppins-light">
                              {user.permission || user.status || "Member"}
                            </p>
                          </div>
                          <div className={`w-2 h-2 rounded-full ${
                            user.status === "verified" || user.verified 
                              ? "bg-green-400 shadow-[0_0_8px_rgba(34,197,94,0.6)]" 
                              : "bg-yellow-400 shadow-[0_0_8px_rgba(234,179,8,0.6)]"
                          }`} />
                        </div>
                      ))}
                      {users.length > 3 && (
                        <div className="text-center pt-2">
                          <span className="text-white/50 text-xs poppins-light">
                            +{users.length - 3} more members
                          </span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <span className="text-white/50 text-sm poppins-light">No team members</span>
                    </div>
                  )}
                </div>
                
                <button
                  onClick={() => window.location.href = '/team'}
                  className="w-full py-2 px-4 glass-button dark:glass-button-dark border border-white/10 rounded-lg text-white/70 hover:text-white/90 hover:border-white/20 transition-all duration-300 text-sm poppins-light mt-3"
                >
                  Members →
                </button>
              </Card>
              
              {mapList.length > 0 && (
                <Card title="Recent Designs" className="p-4 h-fit">
                  <div className="space-y-4">
                    <div className="flex flex-row gap-3 justify-start items-start">
                      {mapList.map(map => (
                        <div
                          key={map.id}
                          className="cursor-pointer flex-1 max-w-[100px] group transition-all duration-300"
                          onClick={() => window.location.href = '/studio'}
                        >
                          <div
                            id={`dashboard-map-${map.id}`}
                            className="w-full aspect-square rounded-lg glass-card dark:glass-card-dark border border-white/10 overflow-hidden group-hover:border-white/20 group-hover:shadow-[0_0_20px_rgba(255,255,255,0.1)] transition-all duration-300"
                          />
                          <p className="text-xs mt-2 text-white/70 font-light poppins-light tracking-wide truncate group-hover:text-white/90 transition-colors">
                            {map.title}
                          </p>
                        </div>
                      ))}
                    </div>
                    
                    <button
                      onClick={() => window.location.href = '/studio'}
                      className="w-full py-2 px-4 glass-button dark:glass-button-dark border border-white/10 rounded-lg text-white/70 hover:text-white/90 hover:border-white/20 transition-all duration-300 text-sm poppins-light"
                    >
                      View All Designs →
                    </button>
                  </div>
                </Card>
              )}
            </div>
          </div>

        </Animate>
      </div>
      <Feedback />
    </div>
  );
}

// --- Free Tier Used card with per-API scope dropdown (live data) ---
function FreeTierStat({ startDate, endDate }) {
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(null);
  const [menuPos, setMenuPos] = useState(null);
  const btnRef = useRef(null);

  // live per-scope usage from the backend (limits mirror the free-tier worker)
  const qs =
    startDate && endDate ? `?startDate=${startDate}&endDate=${endDate}` : "";
  const summary = useAPI(`/api/stripe/usage-summary${qs}`);
  const scopes = summary?.data?.scopes || [];
  const highest = summary?.data?.highest || null;

  // default selection follows the most-at-risk scope until the user picks one
  const cur =
    scopes.find((s) => s.scope === sel) || highest || scopes[0] || null;
  const pct = cur?.percentage ?? 0;
  const ring = pct >= 90 ? "#FF6B6B" : pct >= 75 ? "#FFB36B" : "#5B93FF";

  if (summary?.loading || !cur) {
    return (
      <header className="flex items-center gap-4">
        <div className="w-[60px] h-[60px] shrink-0 rounded-full glass-card dark:glass-card-dark border border-white/10 animate-pulse" />
        <div className="space-y-2">
          <div className="h-5 w-16 rounded bg-white/10 animate-pulse" />
          <div className="h-3 w-24 rounded bg-white/10 animate-pulse" />
        </div>
      </header>
    );
  }

  const toggle = () => {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setMenuPos({ top: r.bottom + 6, right: window.innerWidth - r.right });
    }
    setOpen((o) => !o);
  };

  return (
    <header className="flex items-center gap-4 relative">
      {/* dropdown arrow - top right corner of the card */}
      <button
        ref={btnRef}
        type="button"
        onClick={toggle}
        className="absolute -top-1 -right-1 z-50 p-1 text-white/50 hover:text-white transition-colors"
      >
        <svg
          className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && menuPos &&
        createPortal(
          <>
            {/* click-away backdrop */}
            <div className="fixed inset-0 z-[998]" onClick={() => setOpen(false)} />
            <div
              className="fixed z-[999] w-48 max-h-72 overflow-y-auto rounded-lg glass-card dark:glass-card-dark border border-white/10 backdrop-blur-xl shadow-2xl py-1"
              style={{ top: menuPos.top, right: menuPos.right }}
            >
              {scopes.map((s) => (
                <button
                  key={s.scope}
                  onClick={() => {
                    setSel(s.scope);
                    setOpen(false);
                  }}
                  className={`w-full flex justify-between items-center px-3 py-1.5 text-[12px] text-left hover:bg-white/10 transition-colors ${
                    s.scope === cur.scope ? "text-white" : "text-white/70"
                  }`}
                >
                  <span>{s.label}</span>
                  <span className="text-white/40">{s.percentage}%</span>
                </button>
              ))}
            </div>
          </>,
          document.body
        )}

      <div
        className="flex items-center justify-center w-[60px] h-[60px] shrink-0 rounded-full glass-card dark:glass-card-dark backdrop-blur-lg border border-white/10"
        style={{ boxShadow: `0 0 20px ${ring}55` }}
      >
        <span className="text-[15px] font-[700]" style={{ color: ring }}>
          {pct}%
        </span>
      </div>

      <div className="min-w-0">
        <div className="text-[24px] font-[600] text-white poppins-semibold tracking-wide leading-tight">
          {pct}%
        </div>
        <div className="mt-0.5 text-[14px] font-[300] text-white/70 poppins-light tracking-wide">
          Free tier · {cur.label}
        </div>
        <div className="text-[11px] text-white/40 poppins-light mt-0.5">
          {cur.used.toLocaleString()} / {cur.limit.toLocaleString()} requests
        </div>
      </div>
    </header>
  );
}
